<script setup>
// Check all required fields except Order Reference are set
const allRequiredFieldsSet = computed(() => {
  return !!(
    license.value.name &&
    license.value.customerId &&
    license.value.count &&
    license.value.enddate
  );
});
import { ref, onMounted, defineEmits, defineProps, computed, watch } from "vue";
import { apiQuery } from "@/api/j24api";
import AlertsList from "@/components/AlertsList.vue";
import VSelectSearch from "@/components/VSelectSearch.vue";

const props = defineProps({
  license: Object,
  licenses: Array,
  customers: Array,
  newLicense: Boolean,
});
const license = ref({});
const emit = defineEmits(["Back", "Updated"]);
const selectedTab = ref("details");
const loading = ref(false);
const alerts = ref([]);
const editing = ref(false);
const savedNewLicense = ref(false);

onMounted(() => {
  if (props.newLicense) {
    editing.value = true;
    license.value = {};
    return;
  }
  if (props.license) {
    editing.value = false;
    license.value = props.license;
    savedNewLicense.value = false;
  }
});

watch(
  () => props.license,
  (newVal) => {
    editing.value = false;
    license.value = newVal;
  }
);

watch(
  () => props.newLicense,
  (newVal) => {
    if (newVal) {
      editing.value = true;
      license.value = {};
    }
  }
);

const checkLicenseName = (value) => {
  if (!props.newLicense) return true;
  if (props.licenses.find((lic) => lic.name === value)) {
    return "License name must be unique";
  }
  return true;
};

async function saveLicense() {
  loading.value = true;
  try {
    let payload = {
      type: "Patch24",
      apiAction: props.newLicense ? "CreateNewLicense" : "UpdateLicense",
      license: license.value,
    };
    const result = await apiQuery("ELA", "licences", payload);
    emit("Updated");
    if (!props.newLicense) {
      emit("Back");
    } else {
      if (result.license) {
        license.value = result;
        savedNewLicense.value = true;
      }
    }
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error saving license: " + error,
    });
    loading.value = false;
  }
  loading.value = false;
  //editing.value = false;
}

let currentCustomerName = computed(() => {
  let cust = props.customers.find((c) => c._id === license.value.customerId);
  if (cust) {
    return cust.name;
  } else {
    return "Unknown";
  }
});
</script>

<template>
  <v-tabs v-model="selectedTab" class="mb-3">
    <v-tab value="details">License Details</v-tab>
  </v-tabs>
  <v-window v-model="selectedTab">
    <v-window-item value="details">
      <v-sheet rounded elevation="1" class="pa-3">
        <v-row>
          <v-col cols="12" class="pa-3">
            <v-text-field
              v-model="license.name"
              label="License Name"
              :readonly="!props.newLicense"
              :rules="[checkLicenseName]"
            ></v-text-field>
            <v-text-field
              v-if="!editing"
              readonly
              v-model="currentCustomerName"
              label="Customer"
            ></v-text-field>
            <VSelectSearch
              v-if="editing"
              v-model="license.customerId"
              :items="props.customers"
              item-title="name"
              item-value="_id"
              label="Customer"
              placeholder="Select Customer"
            />
            <v-text-field
              v-model="license.count"
              label="Seats"
              type="number"
              :readonly="!editing"
            >
            </v-text-field>
            <v-text-field
              v-model="license.orderReference"
              label="Order Reference"
              :readonly="!editing"
            />
            <v-date-picker
              v-if="editing"
              v-model="license.enddate"
              title="End Date"
              :readonly="!editing"
            ></v-date-picker>
            <v-text-field
              v-if="!editing"
              v-model="license.formattedEndDate"
              label="End Date"
              readonly
            ></v-text-field>
            <template v-if="license.license">
              <v-text-field
                v-if="license.license.apiKey"
                v-model="license.license.apiKey"
                label="Key"
                readonly
              ></v-text-field>
              <v-textarea
                v-if="license.license.key"
                v-model="license.license.key"
                label="License"
                readonly
                rows="5"
              ></v-textarea>
            </template>
          </v-col>
        </v-row>
        <v-row class="pb-3 pl-3">
          <v-col cols="auto">
            <v-btn
              v-if="savedNewLicense"
              @click="
                emit('Updated');
                emit('Back');
              "
              >Back</v-btn
            >
            <v-btn v-else @click="emit('Back')">Cancel </v-btn>
          </v-col>
          <v-col cols="auto" v-if="!editing && !savedNewLicense"
            ><v-btn @click="editing = !editing">Edit</v-btn></v-col
          >
          <v-col cols="auto" v-if="editing && !savedNewLicense">
            <v-btn
              :loading="loading"
              :disabled="!allRequiredFieldsSet || loading"
              @click="saveLicense"
              >Save</v-btn
            >
          </v-col>
        </v-row>
      </v-sheet>
    </v-window-item>
  </v-window>
</template>
